import type { ApolloError } from '@apollo/client';
import type { Zone } from '@seek/audience-zones';
import { getUrl } from '@seek/ca-myprofile-utils';
import type { Language } from '@seek/melways-sites';

import { useAppConfig } from '../appConfig';
import { log } from '../error-logger';
import {
  logGraphQLFormattedError,
  type BrowserLogExtras,
} from '../error-logger/browserLog';

export const isGraphQLAuthError = (error: ApolloError) => {
  if (error && error.graphQLErrors && error.graphQLErrors.length) {
    return Boolean(
      error.graphQLErrors.find(
        (err) => err.extensions && err.extensions.code === 'UNAUTHENTICATED',
      ),
    );
  }
  return false;
};

export const redirectToSignInPage = (
  zone: Zone,
  language: Language,
  isUnifiedSite: boolean,
) => {
  const { pathname = '', search = '' } = window.location;
  const returnUrl = isUnifiedSite
    ? `returnUrl=${encodeURIComponent(`${pathname}${search}`)}`
    : `rt=${pathname}${search}`;
  return `${getUrl('sign-in', zone, language)}?${returnUrl}`;
};

export const handleError = (
  error: ApolloError,
  zone: Zone,
  language: Language,
  isUnifiedSite: boolean,
  browserLogExtras: BrowserLogExtras,
) => {
  const isAuthError = isGraphQLAuthError(error);
  if (isAuthError && typeof window !== 'undefined') {
    const redirectUrl = redirectToSignInPage(zone, language, isUnifiedSite);
    window.location.href = redirectUrl;
  }

  if (!isAuthError) {
    if (error.graphQLErrors) {
      error.graphQLErrors.forEach((err) => {
        logGraphQLFormattedError(err, browserLogExtras);
      });
    } else {
      log(error, browserLogExtras);
    }
  }
};

export const useHandleError = (
  error: ApolloError | undefined,
  browserLogExtras: BrowserLogExtras,
) => {
  const { zone, language, isUnifiedSite } = useAppConfig();
  if (error) {
    handleError(error, zone, language, isUnifiedSite, browserLogExtras);
  }
};
